import React, { useEffect, useMemo, useState } from 'react';
import { MdAdd, MdDeleteOutline } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import FieldsTable from '../components/fieldsTable';
import Header from '../components/header';
import AppLayout from '../components/AppLayout';
import {
  addBusinessFields,
  deleteBusinessField,
  editBusinessFieldLabel,
  getBusinessFields,
} from '../logic/business/action';
import { useTenant } from '../context/TenantContext';
import { getCustomFieldModules } from '../utils/customFieldModules';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

const emptyField = () => ({ field: '', type: 'text', required: 'false', values: '' });

const FieldScreen = () => {
  const dispatch = useDispatch();
  const selector = useSelector((state) => state.business);
  const selectorAuth = useSelector((state) => state.auth);
  const { tenant } = useTenant();

  const modules = useMemo(() => getCustomFieldModules(tenant), [tenant]);
  const [activeModule, setActiveModule] = useState('customers');

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([emptyField()]);
  const [editField, setEditField] = useState({ field_id: 0, label: '' });
  const [deleteId, setDeleteId] = useState(0);

  useEffect(() => {
    if (selectorAuth?.user?.business_id) {
      getBusinessFields(dispatch);
    }
  }, [selectorAuth?.user?.business_id, dispatch]);

  useEffect(() => {
    if (modules.length && !modules.find((m) => m.key === activeModule)) {
      setActiveModule(modules[0].key);
    }
  }, [modules, activeModule]);

  const current = modules.find((m) => m.key === activeModule) || { key: activeModule, label: activeModule, singular: activeModule };
  const allFields = Array.isArray(selector?.fields) ? selector.fields : [];
  const moduleFields = allFields.filter((f) => (f.module || 'customers') === activeModule);
  const countFor = (key) => allFields.filter((f) => (f.module || 'customers') === key).length;

  const openAdd = () => {
    setRows([emptyField()]);
    setAddOpen(true);
  };

  const updateRow = (index, patch) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const removeRow = (index) => setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  const addRow = () => setRows((prev) => [...prev, emptyField()]);

  const filledRows = rows.filter((r) => r.field.trim());

  const handleAdd = async () => {
    if (filledRows.length === 0) return;
    setSaving(true);
    try {
      await addBusinessFields(
        dispatch,
        filledRows.map((r) => ({ ...r, field: r.field.trim(), module: activeModule })),
        activeModule
      );
      setRows([emptyField()]);
      setAddOpen(false);
    } catch (e) {
      // error toast already shown by the action; keep the dialog open to fix
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!editField.label.trim()) return;
    setSaving(true);
    try {
      await editBusinessFieldLabel(dispatch, editField.field_id, editField.label);
      setEditOpen(false);
    } catch (e) {
      // toast already shown
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setSaving(true);
    try {
      await deleteBusinessField(dispatch, deleteId);
      setDeleteId(0);
      setDeleteOpen(false);
    } catch (e) {
      // toast already shown
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div className="w-full h-full flex flex-col items-start gap-4 p-4">
        <Header
          title="Custom Fields"
          actions={
            <Button variant="contained" startIcon={<MdAdd />} onClick={openAdd} sx={{ textTransform: 'none' }}>
              Add {current.singular} Field
            </Button>
          }
        />

        <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={activeModule}
            onChange={(_, v) => setActiveModule(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {modules.map((m) => (
              <Tab
                key={m.key}
                value={m.key}
                label={`${m.label} (${countFor(m.key)})`}
                sx={{ textTransform: 'none' }}
              />
            ))}
          </Tabs>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
          These fields appear under <strong>More fields</strong> when you add or edit a {current.singular.toLowerCase()}.
        </Typography>

        <FieldsTable
          rows={moduleFields}
          setEditMode={(data) => {
            setEditField({ field_id: data.field_id, label: data.label });
            setEditOpen(true);
          }}
          onDelete={(fieldId) => {
            setDeleteId(fieldId);
            setDeleteOpen(true);
          }}
        />
      </div>

      {/* Add one or more fields */}
      <Dialog open={addOpen} onClose={() => !saving && setAddOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add {current.singular} Fields</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {rows.map((row, index) => (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 2fr auto' },
                  gap: 1.5,
                  alignItems: 'center',
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <TextField
                  label="Field name"
                  placeholder="e.g. Insurance Policy No"
                  value={row.field}
                  onChange={(e) => updateRow(index, { field: e.target.value })}
                  size="small"
                  required
                  autoFocus={index === rows.length - 1}
                />
                <FormControl size="small">
                  <InputLabel>Type</InputLabel>
                  <Select label="Type" value={row.type} onChange={(e) => updateRow(index, { type: e.target.value })}>
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="decimal">Decimal</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="time">Time</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>Required</InputLabel>
                  <Select
                    label="Required"
                    value={row.required}
                    onChange={(e) => updateRow(index, { required: e.target.value })}
                  >
                    <MenuItem value="false">No</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Dropdown options (optional)"
                  placeholder="Gold,Silver,Bronze"
                  value={row.values}
                  onChange={(e) => updateRow(index, { values: e.target.value })}
                  size="small"
                />
                <Tooltip title={rows.length === 1 ? '' : 'Remove'}>
                  <span>
                    <IconButton onClick={() => removeRow(index)} disabled={rows.length === 1} size="small">
                      <MdDeleteOutline />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))}
            <Box>
              <Button startIcon={<MdAdd />} onClick={addRow} sx={{ textTransform: 'none' }}>
                Add another field
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Names are saved in lowercase with underscores (e.g. "Insurance Policy No" becomes insurance_policy_no).
              Leave dropdown options empty for a free-entry field.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving || filledRows.length === 0}>
            {saving ? 'Saving...' : `Save ${filledRows.length || ''} field${filledRows.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit field */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename Field</DialogTitle>
        <DialogContent>
          <TextField
            label="Field name"
            value={editField.label}
            onChange={(e) => setEditField((prev) => ({ ...prev, label: e.target.value }))}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete custom field?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes the field and the values already saved in it. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
};

export default FieldScreen;
